import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  PenTool, ArrowLeft, Send, Star, Clock, 
  CheckCircle, AlertCircle, Loader2, BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const EssayPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [essay, setEssay] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [wordCount, setWordCount] = useState(0);

  const essayPrompts = [
    {
      id: 1,
      subject: 'English',
      keyStage: 'KS2',
      title: 'My Favourite Place',
      prompt: 'Describe your favourite place to visit. What makes it special? Use descriptive language to help the reader imagine being there.',
      minWords: 100,
      maxWords: 300,
      points: 50
    },
    {
      id: 2,
      subject: 'English',
      keyStage: 'KS2',
      title: 'An Adventure Story',
      prompt: 'Write a short adventure story about a character who discovers a hidden door. What happens when they open it?',
      minWords: 150,
      maxWords: 400,
      points: 75
    },
    {
      id: 3,
      subject: 'English',
      keyStage: 'KS3',
      title: 'Persuasive Writing',
      prompt: 'Should school uniforms be mandatory? Write a persuasive essay arguing for or against school uniforms. Include at least three reasons to support your argument.',
      minWords: 200,
      maxWords: 500,
      points: 100
    },
    {
      id: 4,
      subject: 'English',
      keyStage: 'KS3',
      title: 'Letter to the Future',
      prompt: 'Write a letter to yourself in 10 years. What hopes and dreams do you have? What advice would you give your future self?',
      minWords: 150,
      maxWords: 400,
      points: 75
    },
    {
      id: 5,
      subject: 'Science',
      keyStage: 'KS3',
      title: 'Climate Change',
      prompt: 'Explain the causes and effects of climate change. What can individuals do to help reduce its impact?',
      minWords: 200,
      maxWords: 500,
      points: 100
    },
    {
      id: 6,
      subject: 'English',
      keyStage: 'KS4',
      title: 'Character Analysis',
      prompt: 'Choose a character from a book you have read. Analyse how the author presents this character and what makes them memorable.',
      minWords: 250,
      maxWords: 600,
      points: 125
    }
  ];

  useEffect(() => {
    const words = essay.trim().split(/\s+/).filter(w => w.length > 0);
    setWordCount(words.length);
  }, [essay]);

  const selectPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setEssay('');
    setFeedback(null);
  };

  const submitEssay = async () => {
    if (!selectedPrompt) return;
    
    if (wordCount < selectedPrompt.minWords) {
      toast.error(`Please write at least ${selectedPrompt.minWords} words.`);
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await api.post('/essays/submit', {
        prompt_id: selectedPrompt.id,
        prompt_title: selectedPrompt.title,
        prompt_text: selectedPrompt.prompt,
        essay_text: essay,
        word_count: wordCount,
        subject: selectedPrompt.subject.toLowerCase(),
        key_stage: selectedPrompt.keyStage.toLowerCase()
      });
      
      setFeedback({ ...response.data.feedback, points_earned: response.data.points_earned });
      toast.success('Essay submitted successfully!');
    } catch (error) {
      console.error('Error submitting essay:', error);
      toast.error('Failed to submit essay. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetEssay = () => {
    setSelectedPrompt(null);
    setEssay('');
    setFeedback(null);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-500';
  };

  return (
    <StudentLayout>
      <div data-testid="essay-page" className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => selectedPrompt ? resetEssay() : navigate('/student')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            {selectedPrompt ? 'Back to Prompts' : 'Back to Dashboard'}
          </button>
        </div>

        {/* Page Header */}
        <div className="student-card bg-purple-100 border-purple-300 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-purple-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center">
              <PenTool className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-800">Essay Practice</h1>
              <p className="text-slate-600">Write essays and get AI-powered feedback!</p>
            </div>
          </div>
        </div>

        {/* Prompt Selection */}
        {!selectedPrompt && (
          <div className="space-y-4">
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">Choose a Writing Prompt</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {essayPrompts.map((prompt) => (
                <div 
                  key={prompt.id}
                  className="student-card hover:shadow-lg cursor-pointer transition-all"
                  onClick={() => selectPrompt(prompt)}
                  data-testid={`prompt-${prompt.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      prompt.subject === 'English' ? 'bg-pink-100 text-pink-700' : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {prompt.subject} • {prompt.keyStage}
                    </span>
                    <div className="flex items-center gap-1 text-amber-600">
                      <Star className="w-4 h-4" />
                      <span className="text-sm font-bold">{prompt.points}</span>
                    </div>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-slate-800 mb-2">{prompt.title}</h3>
                  <p className="text-slate-600 text-sm mb-3 line-clamp-2">{prompt.prompt}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <BookOpen className="w-4 h-4" />
                    <span>{prompt.minWords}-{prompt.maxWords} words</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Essay Writing */}
        {selectedPrompt && !feedback && (
          <div className="student-card">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  selectedPrompt.subject === 'English' ? 'bg-pink-100 text-pink-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {selectedPrompt.subject} • {selectedPrompt.keyStage}
                </span>
                <div className="flex items-center gap-1 text-amber-600">
                  <Star className="w-4 h-4" />
                  <span className="text-sm font-bold">Up to {selectedPrompt.points} points</span>
                </div>
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-800 mb-3">{selectedPrompt.title}</h2>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-slate-700">{selectedPrompt.prompt}</p>
              </div>
            </div>

            <div className="mb-4">
              <Textarea
                value={essay}
                onChange={(e) => setEssay(e.target.value)}
                placeholder="Start writing your essay here..."
                className="min-h-[300px] text-lg"
                data-testid="essay-textarea"
              />
            </div>

            <div className="flex items-center justify-between mb-6">
              <div className={`flex items-center gap-2 ${
                wordCount < selectedPrompt.minWords ? 'text-amber-600' : 
                wordCount > selectedPrompt.maxWords ? 'text-red-500' : 'text-emerald-600'
              }`}>
                <BookOpen className="w-5 h-5" />
                <span className="font-semibold">
                  {wordCount} / {selectedPrompt.minWords}-{selectedPrompt.maxWords} words
                </span>
              </div>
              
              {wordCount < selectedPrompt.minWords && (
                <span className="text-sm text-slate-500">
                  Write at least {selectedPrompt.minWords - wordCount} more words
                </span>
              )}
            </div>

            <Button 
              onClick={submitEssay}
              disabled={isSubmitting || wordCount < selectedPrompt.minWords}
              className="w-full student-btn"
              data-testid="submit-essay-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Analysing your essay...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Submit for Feedback
                </>
              )}
            </Button>
          </div>
        )}

        {/* Feedback */}
        {feedback && (
          <div className="space-y-6">
            {/* Score Card */}
            <div className="student-card bg-gradient-to-r from-purple-100 to-pink-100 border-purple-300">
              <div className="text-center">
                <h2 className="font-heading text-2xl font-bold text-slate-800 mb-4">Your Results</h2>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <span className={`font-heading text-6xl font-bold ${getScoreColor(feedback.overall_score)}`}>
                    {feedback.overall_score}
                  </span>
                  <span className="text-slate-500 text-2xl">/ 100</span>
                </div>
                <div className="flex items-center justify-center gap-2 mb-6">
                  <Star className="w-6 h-6 text-amber-400 fill-amber-400" />
                  <span className="font-heading text-2xl font-bold text-amber-600">
                    +{feedback.points_earned} points earned!
                  </span>
                </div>
              </div>
            </div>

            {/* Detailed Feedback */}
            <div className="student-card">
              <h3 className="font-heading text-xl font-bold text-slate-800 mb-4">Detailed Feedback</h3>
              
              {/* Strengths */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <h4 className="font-bold text-emerald-700">Strengths</h4>
                </div>
                <ul className="space-y-2 pl-7">
                  {feedback.strengths.map((strength, i) => (
                    <li key={i} className="text-slate-700 list-disc">{strength}</li>
                  ))}
                </ul>
              </div>

              {/* Areas for Improvement */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <h4 className="font-bold text-amber-700">Areas for Improvement</h4>
                </div>
                <ul className="space-y-2 pl-7">
                  {feedback.improvements.map((improvement, i) => (
                    <li key={i} className="text-slate-700 list-disc">{improvement}</li>
                  ))}
                </ul>
              </div>

              {/* Criteria Scores */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-bold text-slate-800 mb-3">Marking Criteria</h4>
                <div className="space-y-3">
                  {Object.entries(feedback.criteria_scores).map(([criterion, score]) => (
                    <div key={criterion} className="flex items-center justify-between">
                      <span className="text-slate-600 capitalize">{criterion.replace('_', ' ')}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-slate-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              score >= 80 ? 'bg-emerald-500' : 
                              score >= 60 ? 'bg-amber-500' : 'bg-red-400'
                            }`}
                            style={{ width: `${score}%` }}
                          />
                        </div>
                        <span className="font-bold text-slate-700 w-10">{score}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={resetEssay}>
                <PenTool className="w-4 h-4 mr-2" />
                Write Another Essay
              </Button>
              <Button onClick={() => navigate('/student')} className="student-btn">
                Back to Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default EssayPage;
